/**
 * The camera on the printer's lid: a 48 MP USB camera plugged into the
 * workstation and read by the browser (getUserMedia), not by OpenCV -- the
 * solver runs in AWS and cannot see a camera on this desk.
 *
 * Only the pure parts live here, so `test/logic.ts` can cover them.
 */

/** 48 MP at 4:3. Asked for as `ideal`, so the browser picks the nearest mode it has. */
export const SENSOR = { width: 8000, height: 6000 };

/**
 * Below this a still is not worth sending. A 19.2" platen framed tightly at
 * 4K is about 200 px/inch, comfortably over the solver's 8 px/hole floor; at
 * 1080p it is not, and a browser that quietly fell back to 1080p (a busy
 * device, a USB 2 port) should be caught here rather than in a refusal.
 */
export const MIN_LONG_SIDE_PX = 3840;

export function videoConstraints(deviceId: string | null): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      width: { ideal: SENSOR.width },
      height: { ideal: SENSOR.height },
    },
  };
}

/** "8000 × 6000 (48 MP)" */
export function describeSize(width: number, height: number): string {
  const mp = (width * height) / 1e6;
  return `${width} × ${height} (${mp >= 10 ? Math.round(mp) : mp.toFixed(1)} MP)`;
}

export function tooSmall(width: number, height: number): boolean {
  return Math.max(width, height) < MIN_LONG_SIDE_PX;
}

/** The remembered camera, if this browser still has it plugged in. */
export function pickDevice(devices: { deviceId: string }[],
                           remembered: string | null): string | null {
  if (remembered && devices.some((d) => d.deviceId === remembered)) return remembered;
  return devices[0]?.deviceId ?? null;
}

/**
 * The remembered camera is not plugged in. It is asked for by exact id, and a
 * browser that has no such device refuses the whole request rather than open
 * another, so a swapped camera, a cleared site or a different USB port left
 * the button failing on every press, with no camera list to choose from.
 */
export function deviceGone(e: unknown): boolean {
  const name = (e as { name?: unknown } | null)?.name;
  return name === 'OverconstrainedError' || name === 'NotFoundError';
}

/**
 * The camera was found but sent no frame before the browser gave up on it
 * ("AbortError: Timeout starting video source").
 *
 * What did it on 2026-10-16, on every page, in Edge on the workstation: a USB
 * camera keeps its exposure in its own hardware until it loses power, so a
 * long exposure set on the slider outlived the page that set it, and at 48 MP
 * the camera could not get a frame out in time. Restarting Edge did nothing;
 * opening the Windows Camera app, which puts exposure back on automatic, did.
 */
export function startTimedOut(e: unknown): boolean {
  return (e as { name?: unknown } | null)?.name === 'AbortError';
}

/**
 * A small stream, opened only to put the camera's exposure back on automatic
 * when the full-size one will not start: at this size a slow exposure still
 * gets a frame out in time, which is how the Camera app managed it.
 */
export function resetConstraints(deviceId: string | null): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      width: { ideal: 640 },
      height: { ideal: 480 },
    },
  };
}

// -- capture timing --------------------------------------------------------

/**
 * How long one press of a shoot button took, sent with the upload request so
 * it lands in CloudWatch beside the solver's own `timing` line. The server
 * sees nothing before the upload URL is asked for, and the client reported a
 * minute or two from click to result when the server accounted for ~35 s.
 *
 * `steps` are milliseconds, each from the end of the one before: `open`,
 * `settle`, `photo`, `encode`, `release` (blind shots open and release the
 * camera; a shot from the preview does neither).
 */
export interface CaptureTiming {
  mode: 'blind' | 'preview';
  /** `imagecapture` for a driver photo, `frame` for the video-frame fallback. */
  method?: 'imagecapture' | 'frame';
  /** The full-size start timed out and was retried after a reset. */
  retried?: boolean;
  width?: number;
  height?: number;
  steps: Record<string, number>;
  totalMs: number;
}

export interface CaptureClock {
  timing: CaptureTiming;
  lap(step: string): void;
  /** The timing with `totalMs` filled in; call once the still is in hand. */
  done(): CaptureTiming;
}

export function captureClock(mode: CaptureTiming['mode'],
                             now: () => number = () => performance.now()): CaptureClock {
  const start = now();
  let last = start;
  const timing: CaptureTiming = { mode, steps: {}, totalMs: 0 };
  return {
    timing,
    lap(step) {
      const t = now();
      timing.steps[step] = Math.round(t - last);
      last = t;
    },
    done() {
      timing.totalMs = Math.round(now() - start);
      return timing;
    },
  };
}

/** How long to let the camera settle between the reset and the real start. */
export const RESET_SETTLE_MS = 500;

/** What getUserMedia's refusal means at the desk; its own message is often empty. */
export function describeOpenError(e: unknown): string {
  const { name, message } = (e ?? {}) as { name?: string; message?: string };
  const why: Record<string, string> = {
    NotAllowedError: 'the browser is blocking the camera for this site; allow it from the padlock in the address bar',
    NotFoundError: 'no camera is plugged in, or the computer does not see it',
    NotReadableError: 'another program (or another tab) is using the camera; close it and try again',
    OverconstrainedError: 'the camera cannot give what was asked of it',
    AbortError: 'the camera did not start in time, even after its exposure was put '
      + 'back on automatic. Open the Windows Camera app on it once, close it, '
      + 'and try again; if it still fails, unplug it and plug it back in',
  };
  const said = message ? ` (${name}: ${message})` : name ? ` (${name})` : '';
  return `${(name && why[name]) ?? 'it gave no reason'}${said}`;
}

// -- exposure: automatic ---------------------------------------------------

/**
 * The lid camera's exposure is left on automatic, and steadied by fixed
 * lighting on the lid rather than by the browser.
 *
 * It was locked from 2026-10-08, when every sheet was refused
 * MARKERS_NOT_FOUND at 11-14 mil: the white calibration paper and the canvas
 * were exposed differently, so each marker's black square read 1-4 px larger
 * or smaller round its own centre. The lock was taken out because holding a
 * manual exposure stopped the camera opening in the browser.
 *
 * Only putting it back on automatic is left, for a camera still holding a
 * manual exposure in its hardware (see `startTimedOut`). Exposure modes are
 * Image Capture constraints, not in TypeScript's DOM library, so read loosely.
 */
export function offersAutoExposure(caps: object | undefined): boolean {
  const modes = ((caps ?? {}) as Record<string, unknown>).exposureMode;
  return Array.isArray(modes) && modes.includes('continuous');
}

export function autoExposureConstraints(): MediaTrackConstraints {
  return { advanced: [{ exposureMode: 'continuous' }] } as unknown as MediaTrackConstraints;
}
